import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, signal, ViewChild, ViewEncapsulation, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { LinkModule } from '../../models/linkModule.model';
import { LinkModuleService } from '../../services/link module/link module.service';
import {
  TargetDataService,
  TargetItem,
  TargetModule
} from '../../services/link module/target-data.service';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { AppModuleName } from '../../enums/linkModule';
import { ToastService } from '../../services/toast/toast.service';
import { PermissionService } from '../../../core/services/permission.service';
import { menuDisplayName } from '../../../core/services/menu.service';

/** A link row as the API returns it; the id and the other side come back in more than one shape. */
interface ApiLinkRow {
  Id?: number;
  id?: number;
  otherSide?: { module?: string; recordId?: number };
  targetModule?: string;
  targetRecordId?: number;
}

export interface LinkPickerRow {
  linkId: number;
  targetRecordId: number;
  displayText: string;
  isLinked: boolean; // false = staged, true = saved in DB
}

@Component({
  selector: 'app-link-module',
  templateUrl: './link-module.component.html',
  styleUrls: ['./link-module.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // eslint-disable-next-line @angular-eslint/prefer-standalone -- pre-existing NgModule-declared component; standalone conversion is a separate migration (COL-37)
  standalone: false
})
export class LinkModuleComponent implements OnInit {
  private fb = inject(FormBuilder);
  private linkSvc = inject(LinkModuleService);
  private targetData = inject(TargetDataService);
  private toastService = inject(ToastService);
  private permSvc = inject(PermissionService);
  dialogRef? = inject<MatDialogRef<LinkModuleComponent>>(MatDialogRef);
  private cd = inject(ChangeDetectorRef);
  dialogData? = inject<{
    currentModule: string;
    currentRecordId: number;
    userId: number;
    headerMsg?: string;
}>(MAT_DIALOG_DATA);

  // === Inputs (inline mode) ===
  @Input() currentModule!: string;
  @Input() currentRecordId!: number;
  @Input() userId!: number;

  // === Outputs (inline mode) ===
  @Output() linksChanged = new EventEmitter<LinkPickerRow[]>();

  @ViewChild('autoTrigger') autoTrigger!: MatAutocompleteTrigger;

  // === Form ===
  linkModuleForm!: FormGroup;

  // === State ===
  loading = signal(false);
  error = signal<string | null>(null);

  targetModules: TargetModule[];
  selectedTargetModule: TargetModule = AppModuleName.Template;
  currentItems: TargetItem[] = [];
  rows: LinkPickerRow[] = [];
  selectedModuleData: number[] = [];
  searchCtrl = new FormControl('');


  constructor() {
    const dialogData = this.dialogData;

    // Override inputs if dialog data provided
    if (dialogData) {
      this.currentModule = dialogData.currentModule;
      this.currentRecordId = dialogData.currentRecordId;
      this.userId = dialogData.userId;
    }
  }
  // When user presses Enter in search
  onEnterSelectFirst(trigger: MatAutocompleteTrigger) {
    const results = this.filteredItems();
    if (results.length > 0) {
      const first = results[0];
      if (!this.isSelected(first.id)) {
        this.toggleFromSearch(first, true);
      }
      // Clear search input after selection
      this.searchCtrl.setValue('');
      trigger.closePanel();
    }
  }
  private canViewModule(module: AppModuleName): boolean {
    if (this.permSvc.isSuperAdmin() || this.permSvc.isOwner()) return true;
    const menuId = this.permSvc.getIdByName(module as string);
    if (menuId === null) return true; // not in permission system yet — allow
    return this.permSvc.can(menuId, 'view');
  }

  private filterByPermission(modules: AppModuleName[]): AppModuleName[] {
    return modules.filter(m => this.canViewModule(m));
  }

  /** Server module name as the user knows it (Work Package is shown as Site). */
  displayModule(module: string): string {
    return menuDisplayName(module);
  }

  async ngOnInit() {
    switch (this.currentModule) {
      case AppModuleName.WorkOrderType:
        this.targetModules = this.filterByPermission([AppModuleName.Template]);
        break;
      case AppModuleName.WorkOrder:
        this.targetModules = this.filterByPermission([AppModuleName.Template, AppModuleName.Schedular, AppModuleName.BootsOnGround]);
        break;
      case AppModuleName.Action:
        this.targetModules = this.filterByPermission([AppModuleName.WorkOrder, AppModuleName.WorkOrderType, AppModuleName.WorkPackage,
                  AppModuleName.Schedular, AppModuleName.Booking, AppModuleName.BootsOnGround,
                  AppModuleName.Template, AppModuleName.Inspection]);
        break;
      default:
        this.targetModules = this.filterByPermission([AppModuleName.Template]);
    }
    this.linkModuleForm = this.fb.group({
      targetModule: [this.selectedTargetModule],
      targetIds: [[]]
    });
    // Listen to search changes
    this.searchCtrl.valueChanges.subscribe(() => {
      // Just triggers UI update
    });
    await this.loadModuleItems(this.selectedTargetModule);
    await this.fetchExisting();
  }
  // === Helpers ===
  public trackById = (_: number, id: number) => id;

  public toDisplayName(id: number): string {
    const found = this.currentItems.find(o => String(o.id) === String(id));
    return found ? found.name : `${this.selectedTargetModule} #${id}`;
  }
  // === Search filter ===
  filteredItems(): TargetItem[] {
    const searchText = this.searchCtrl.value?.toLowerCase() || '';
    if (!searchText) return this.currentItems;
    return this.currentItems.filter(item =>
      item.name.toLowerCase().includes(searchText)
    );
  }

  toggleFromSearch(item: TargetItem, force?: boolean) {
    const exists = this.rows.find(r => r.targetRecordId === item.id);
    if (exists && (force === false || force === undefined)) {
      // remove
      this.rows = this.rows.filter(r => r.targetRecordId !== item.id);
      this.linkModuleForm.patchValue({
        targetIds: this.rows.map(r => r.targetRecordId)
      });
    } else if (!exists && (force === true || force === undefined)) {
      // add
      this.rows.push({
        linkId: 0,
        targetRecordId: item.id,
        displayText: item.name,
        isLinked: false
      });
      this.linkModuleForm.patchValue({
        targetIds: this.rows.map(r => r.targetRecordId)
      });
    }
  }
  isSelected(id: number): boolean {
    return this.rows.some(r => r.targetRecordId === id);
  }
  // === Target module switching ===
  public async onTargetModuleChanged(mod: TargetModule) {
    if (this.selectedTargetModule === mod) return;
    this.selectedTargetModule = mod;

    // Reset IDs when module changes
    this.linkModuleForm.patchValue({ targetIds: [] });

    await this.loadModuleItems(mod);
    await this.fetchExisting();
  }

  private async loadModuleItems(mod: TargetModule) {
    this.loading.set(true);
    try {
      this.currentItems = await firstValueFrom(this.targetData.getByModule(mod));
    } 
    catch {
      this.toastService.showError('Error loading module items');
    }
    finally {
      this.loading.set(false);
    }
  }

  // === Server sync ===
  private mapRows(apiRows: ApiLinkRow[]): LinkPickerRow[] {
    const filtered = apiRows
      .map((r) => {
        const otherModule = r.otherSide?.module ?? r.targetModule;
        const otherId = r.otherSide?.recordId ?? r.targetRecordId;
        return { linkId: r.Id ?? r.id, otherModule, otherId };
      })
      .filter(x => x.otherModule === this.selectedTargetModule);

    return filtered.map(x => ({
      linkId: x.linkId,
      targetRecordId: x.otherId,
      displayText: this.toDisplayName(x.otherId),
      isLinked: true
    }));
  }

  public async fetchExisting() {
    if (!this.currentRecordId || !this.currentModule) return;
    this.loading.set(true);
    this.error.set(null);
    try {
      const res = await firstValueFrom(
        this.linkSvc.getList({
          recordId: this.currentRecordId,
          moduleName: this.currentModule
        })
      );
      this.rows = this.mapRows(res ?? []);
      this.cd.detectChanges();  // <-- force UI refresh
      // Sync form multiselect
      this.linkModuleForm.patchValue({
        targetIds: this.rows.map(r => r.targetRecordId)
      });

    } catch {
      this.error.set('Unable to load links.');
    } finally {
      this.loading.set(false);
    }
  }

  private async createLink(targetId: number) {
    await firstValueFrom(
      this.linkSvc.insertUpdate({
        sourceModule: this.currentModule,
        sourceRecordId: this.currentRecordId,
        targetModule: this.selectedTargetModule,
        targetRecordId: targetId,
        userId: this.userId
      } as LinkModule)
    );
  }

  private async deleteLink(linkId: number) {
    await firstValueFrom(this.linkSvc.delete(linkId));
  }

  // === UI events ===
  public onSelectChanged(e: { value?: number[] }) {
    const newIds: number[] = e?.value ?? [];
    const existingIds = this.rows.map(r => r.targetRecordId);
    this.selectedModuleData = newIds;
    // Add staged
    newIds.forEach(id => {
      if (!existingIds.includes(id)) {
        this.rows.push({
          linkId: 0,
          targetRecordId: id,
          displayText: this.toDisplayName(id),
          isLinked: false
        });
      }
    });

    // Remove staged if unselected
    this.rows = this.rows.filter(
      r => newIds.includes(r.targetRecordId) || r.isLinked
    );
  }

  public async onTableActionClick(ev: { command?: { type?: string }; rowData?: LinkPickerRow }) {
    if (!ev?.command?.type || !ev?.rowData) return;

    if (ev.command.type === 'delete') {
      if (ev.rowData.isLinked) {
        await this.deleteLink(ev.rowData.linkId);
        await this.fetchExisting();
      } else {
        this.rows = this.rows.filter(
          r => r.targetRecordId !== ev.rowData.targetRecordId
        );
        this.linkModuleForm.patchValue({
          targetIds: this.rows.map(r => r.targetRecordId)
        });
      }
    }

    if (ev.command.type === 'link') {
      if (!ev.rowData.isLinked) {
        await this.createLink(ev.rowData.targetRecordId);
        await this.fetchExisting();
      }
    }
  }

  public removeChip(id: number): void {
    this.rows = this.rows.filter(r => r.targetRecordId !== id);
    this.linkModuleForm.patchValue({
      targetIds: this.rows.map(r => r.targetRecordId)
    });
  }


  public closeDialog() {
    if (this.dialogRef) this.dialogRef.close(this.rows);
  }

  public async onSubmit() {
    if (!this.rows || this.rows.length === 0) {
      this.error.set('No items selected to link.');
      return;
    }

    this.loading.set(true);
    try {
      // Loop through staged rows (not yet linked in DB)
      for (const row of this.rows) {
        if (!row.isLinked) {
          await firstValueFrom(
            this.linkSvc.insertUpdate({
              sourceModule: this.currentModule,
              sourceRecordId: this.currentRecordId,
              targetModule: this.selectedTargetModule,
              targetRecordId: row.targetRecordId,
              userId: this.userId
            } as LinkModule)
          );
        }
      }

      // Refresh the data to sync with DB
      await this.fetchExisting();

      // Close dialog if opened as dialog
      if (this.dialogRef) {
        this.dialogRef.close(this.rows);
      } else {
        this.linksChanged.emit(this.rows);
      }
    } catch (err) {
      console.error('Error linking modules:', err);
      this.error.set('Failed to save links. Please try again.');
    } finally {
      this.loading.set(false);
    }
  }
  public async onDelete(row: LinkPickerRow) {
    if (row.isLinked) {
      // Remove from backend and refresh
      await this.deleteLink(row.linkId);
      await this.fetchExisting();
    } else {
      // Just remove staged row
      this.rows = this.rows.filter(r => r.targetRecordId !== row.targetRecordId);
      this.linkModuleForm.patchValue({
        targetIds: this.rows.map(r => r.targetRecordId)
      });
    }
  }

}
